import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtNumber, fmtDate } from "@/lib/api";
import { AlertChip } from "@/components/AlertChip";
import OrdenCompraDialog from "@/components/OrdenCompraDialog";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  Package,
  CurrencyDollar,
  Bell,
  Plus,
  ArrowUp,
  ArrowDown,
  TrendUp,
  TrendDown,
  CircleNotch,
  FileText,
  ArrowsLeftRight,
  CaretRight,
  WarningCircle,
  ShieldCheck,
  ShieldWarning,
  Tag,
  Stack,
  ShoppingCart,
} from "@phosphor-icons/react";

function StatCard({ icon: Icon, label, value, accent, sub, to, testid }) {
  const content = (
    <div
      data-testid={testid}
      className="bg-white border border-[#E2E8F0] rounded-md p-6 flex flex-col gap-3 transition-all duration-200 hover:-translate-y-1 hover:shadow-md"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-[0.15em] text-[#64748B]">{label}</span>
        <span className="w-9 h-9 rounded-md flex items-center justify-center" style={{ backgroundColor: accent.bg }}>
          <Icon size={18} weight="bold" style={{ color: accent.fg }} />
        </span>
      </div>
      <div className="text-4xl font-black tracking-tighter text-[#0F172A] font-heading">{value}</div>
      {sub && <p className="text-sm text-[#64748B]">{sub}</p>}
    </div>
  );
  return to ? <Link to={to}>{content}</Link> : content;
}

function QuickAccess({ icon: Icon, label, to, accent, testid }) {
  return (
    <Link to={to} data-testid={testid}>
      <div className="group bg-white border border-[#E2E8F0] rounded-md px-5 py-4 flex items-center gap-3 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md">
        <span className="w-10 h-10 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: accent.bg }}>
          <Icon size={20} weight="bold" style={{ color: accent.fg }} />
        </span>
        <span className="font-semibold text-[#0F172A] flex-1">{label}</span>
        <CaretRight size={16} weight="bold" className="text-[#94A3B8] group-hover:text-[#0F172A] transition-colors" />
      </div>
    </Link>
  );
}

function ListCard({ title, subtitle, icon: Icon, accent, children, empty, hasItems, testid }) {
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-md flex flex-col" data-testid={testid}>
      <div className="px-5 py-4 border-b border-[#E2E8F0] flex items-center gap-2.5">
        <span className="w-8 h-8 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: accent.bg }}>
          <Icon size={16} weight="bold" style={{ color: accent.fg }} />
        </span>
        <div className="min-w-0">
          <h2 className="font-heading font-bold text-[#0F172A] leading-tight">{title}</h2>
          {subtitle && <p className="text-xs text-[#64748B]">{subtitle}</p>}
        </div>
      </div>
      {hasItems ? (
        <ul className="divide-y divide-[#F1F5F9] flex-1">{children}</ul>
      ) : (
        <p className="px-5 py-8 text-center text-sm text-[#64748B] flex-1">{empty}</p>
      )}
    </div>
  );
}

function RankRow({ index, nombre, sub, value, valueColor }) {
  return (
    <li className="flex items-center gap-3 px-5 py-2.5 hover:bg-[#F8F9FA] transition-colors">
      <span className="w-6 h-6 rounded-full bg-[#F1F5F9] text-[#475569] text-xs font-bold flex items-center justify-center shrink-0">
        {index}
      </span>
      <div className="min-w-0 flex-1">
        <div className="font-medium text-[#0F172A] text-sm truncate">{nombre}</div>
        {sub && <div className="text-xs text-[#64748B] truncate">{sub}</div>}
      </div>
      <span className="text-sm font-bold font-heading shrink-0" style={{ color: valueColor || "#0F172A" }}>{value}</span>
    </li>
  );
}

function KpiCard({ label, value, sub, accent, to, testid }) {
  const content = (
    <div data-testid={testid} className="bg-white border border-[#E2E8F0] rounded-md p-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md h-full">
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B] leading-tight">{label}</p>
      <p className="text-2xl font-black tracking-tight font-heading mt-2" style={{ color: accent }}>{value}</p>
      {sub && <p className="text-[11px] text-[#94A3B8] mt-0.5">{sub}</p>}
    </div>
  );
  return to ? <Link to={to}>{content}</Link> : content;
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [tendencia, setTendencia] = useState([]);
  const [ranking, setRanking] = useState(null);
  const [kpis, setKpis] = useState(null);
  const [integridad, setIntegridad] = useState(null);
  const [ocProducto, setOcProducto] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data));
    api.get("/dashboard/tendencia").then((r) => setTendencia(r.data));
    api.get("/ranking").then((r) => setRanking(r.data));
    api.get("/inventario/kpis").then((r) => setKpis(r.data));
    api.get("/inventario/integridad").then((r) => setIntegridad(r.data));
  }, []);

  if (!data) {
    return (
      <div className="flex items-center justify-center py-32">
        <CircleNotch size={32} className="animate-spin text-[#0F172A]" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-up" data-testid="dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-[#0F172A]">Dashboard</h1>
          <p className="text-[#475569] mt-1">Control operacional de inventario, compras y abastecimiento.</p>
        </div>
        <Link to="/productos" data-testid="dashboard-new-batch-link">
          <button className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-[#0F172A] hover:bg-[#1E293B] text-white font-semibold text-sm transition-all duration-200">
            <Plus size={18} weight="bold" /> Nuevo lote
          </button>
        </Link>
      </div>

      {/* Accesos rápidos */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="quick-access">
        <QuickAccess
          testid="quick-reporte"
          icon={FileText}
          label="Generar reporte mensual"
          to="/reporte"
          accent={{ bg: "#EEF2FF", fg: "#3730A3" }}
        />
        <QuickAccess
          testid="quick-productos"
          icon={Package}
          label="Productos"
          to="/productos"
          accent={{ bg: "#F1F5F9", fg: "#0F172A" }}
        />
        <QuickAccess
          testid="quick-movimientos"
          icon={ArrowsLeftRight}
          label="Movimientos"
          to="/movimientos"
          accent={{ bg: "#ECFDF5", fg: "#065F46" }}
        />
        <QuickAccess
          testid="quick-ranking"
          icon={TrendUp}
          label="Ranking"
          to="/ranking"
          accent={{ bg: "#FFF7ED", fg: "#9A3412" }}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          testid="stat-stock-total"
          icon={Package}
          label="Stock total"
          value={fmtNumber(data.stock_total)}
          sub={`${data.total_lotes} lotes registrados`}
          accent={{ bg: "#F1F5F9", fg: "#0F172A" }}
        />
        <StatCard
          testid="stat-valor-inventario"
          icon={CurrencyDollar}
          label="Valor inventario"
          value={fmtMoney(data.valor_inventario)}
          sub={`Costo prom. ${fmtMoney(data.costo_promedio)}/u`}
          accent={{ bg: "#ECFDF5", fg: "#065F46" }}
        />
        <StatCard
          testid="stat-alertas"
          icon={Bell}
          label="Alertas activas"
          value={fmtNumber(data.alertas_activas)}
          sub="Lotes próximos a vencer"
          accent={{ bg: "#FFF7ED", fg: "#9A3412" }}
          to="/alertas"
        />
        <StatCard
          testid="stat-ranking"
          icon={TrendUp}
          label="Rotación"
          value="Ranking"
          sub="Ver top y baja rotación"
          accent={{ bg: "#EFF6FF", fg: "#1E40AF" }}
          to="/ranking"
        />
      </div>

      {/* KPIs de inventario (CMP) */}
      {kpis && (
        <div data-testid="kpi-inventario">
          <h2 className="font-heading text-lg font-bold text-[#0F172A] mb-3">KPIs de inventario (CMP)</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <KpiCard testid="kpi-valor-total" label="Valor inventario" value={fmtMoney(kpis.valor_total_inventario)} accent="#0F172A" to="/inventario" />
            <KpiCard testid="kpi-capital" label="Capital inmovilizado" value={fmtMoney(kpis.capital_inmovilizado)} sub={`≥${kpis.umbral_inmovilizado_dias}d sin salida`} accent="#991B1B" to="/inventario" />
            <KpiCard testid="kpi-rotacion" label="Rotación (12m)" value={`${fmtNumber(kpis.rotacion)}×`} accent="#1E40AF" />
            <KpiCard testid="kpi-cobertura" label="Cobertura" value="—" sub="con ventas" accent="#64748B" />
            <KpiCard testid="kpi-sin-mov" label="Sin movimiento" value={fmtNumber(kpis.productos_sin_movimiento)} accent="#9A3412" to="/inventario" />
            <KpiCard testid="kpi-bajo-stock" label="Bajo stock" value={fmtNumber(kpis.productos_bajo_stock)} accent="#B45309" to="/alertas" />
          </div>
        </div>
      )}

      {/* Integridad del inventario */}
      {integridad && (
        <Link to="/integridad" data-testid="dashboard-integridad-card">
          <div className={`rounded-md border p-5 flex items-center gap-4 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md ${
            integridad.estado === "ok" ? "bg-[#ECFDF5] border-[#A7F3D0]"
              : integridad.estado === "critico" ? "bg-[#FEF2F2] border-[#FECACA]"
              : "bg-[#FFF7ED] border-[#FED7AA]"}`}>
            <span className="w-12 h-12 rounded-md flex items-center justify-center shrink-0"
              style={{ backgroundColor: "#ffffffaa" }}>
              {integridad.estado === "ok"
                ? <ShieldCheck size={26} weight="bold" className="text-[#065F46]" />
                : <ShieldWarning size={26} weight="bold" className={integridad.estado === "critico" ? "text-[#991B1B]" : "text-[#9A3412]"} />}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#64748B]">Integridad del inventario</p>
              <p className="text-2xl font-black font-heading tracking-tight"
                style={{ color: integridad.estado === "ok" ? "#065F46" : integridad.estado === "critico" ? "#991B1B" : "#9A3412" }}>
                {fmtNumber(integridad.integridad_pct)}%
              </p>
              <p className="text-xs text-[#64748B]">
                {integridad.num_lotes_afectados === 0
                  ? "Stock del sistema cuadra con el ledger"
                  : `${integridad.num_lotes_afectados} lote(s) · ${fmtMoney(integridad.valor_diferencia)} en diferencia`}
              </p>
            </div>
            <CaretRight size={18} weight="bold" className="text-[#94A3B8] shrink-0" />
          </div>
        </Link>
      )}

      {/* Alerta de stock crítico de productos */}
      <div
        className={`rounded-md border ${
          data.stock_critico && data.stock_critico.length > 0
            ? "bg-[#FEF2F2] border-[#FECACA]"
            : "bg-white border-[#E2E8F0]"
        }`}
        data-testid="stock-critico-alert"
      >
        <div className="px-6 py-4 flex items-center gap-2.5 border-b border-[#0000000d]">
          <span className="w-8 h-8 rounded-md flex items-center justify-center shrink-0 bg-[#FEE2E2]">
            <WarningCircle size={18} weight="bold" className="text-[#991B1B]" />
          </span>
          <div>
            <h2 className="font-heading font-bold text-[#0F172A] leading-tight">Stock crítico de productos</h2>
            <p className="text-xs text-[#64748B]">Productos en o por debajo de su punto de reorden</p>
          </div>
        </div>
        {data.stock_critico && data.stock_critico.length > 0 ? (
          <div className="flex flex-wrap gap-2 p-4">
            {data.stock_critico.map((p) => (
              <div
                key={p.id}
                data-testid={`critico-${p.id}`}
                className="inline-flex items-center gap-3 bg-white border border-[#FECACA] rounded-md pl-3 pr-2 py-2"
              >
                <Link to={`/productos/${p.id}`} className="flex items-center gap-2 hover:opacity-80">
                  <span className="text-sm font-semibold text-[#0F172A]">{p.nombre}</span>
                  <span className="text-xs text-[#64748B]">{p.codigo}</span>
                  <span className="text-sm font-bold text-[#991B1B] font-heading">{fmtNumber(p.stock_total)} u</span>
                  <span className="text-xs text-[#94A3B8]">/ mín {fmtNumber(p.reorder_point)}</span>
                </Link>
                <button
                  data-testid={`generar-oc-${p.id}`}
                  onClick={() => setOcProducto(p)}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-white bg-[#00CCF1] hover:bg-[#00B8DA] rounded px-2.5 py-1.5 transition-colors"
                >
                  <ShoppingCart size={13} weight="bold" /> Generar OC
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="px-6 py-6 text-sm text-[#64748B]">✓ Ningún producto en stock crítico.</p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 bg-white border border-[#E2E8F0] rounded-md p-6" data-testid="trend-chart">
          <h2 className="font-heading text-lg font-bold text-[#0F172A] mb-1">Tendencia de movimientos</h2>
          <p className="text-sm text-[#64748B] mb-4">Ingresos vs. salidas (últimos 6 meses)</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={tendencia} barGap={6}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F6" vertical={false} />
              <XAxis dataKey="mes" tick={{ fontSize: 12, fill: "#64748B" }} axisLine={{ stroke: "#E2E8F0" }} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#64748B" }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }}
                cursor={{ fill: "#F8F9FA" }}
              />
              <Legend wrapperStyle={{ fontSize: 13 }} />
              <Bar dataKey="ingresos" name="Ingresos" fill="#10B981" radius={[4, 4, 0, 0]} />
              <Bar dataKey="salidas" name="Salidas" fill="#0F172A" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="lg:col-span-2 bg-white border border-[#E2E8F0] rounded-md p-6 flex flex-col">
          <h2 className="font-heading text-lg font-bold text-[#0F172A] mb-4">Resumen de valor</h2>
          <div className="space-y-4 flex-1">
            <div className="flex items-center justify-between py-3 border-b border-[#E2E8F0]">
              <span className="text-sm text-[#64748B]">Valor del inventario</span>
              <span className="text-lg font-bold text-[#0F172A] font-heading">{fmtMoney(data.valor_inventario)}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-[#E2E8F0]">
              <span className="text-sm text-[#64748B]">Costo promedio ponderado</span>
              <span className="text-lg font-bold text-[#0F172A] font-heading">{fmtMoney(data.costo_promedio)}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-[#E2E8F0]">
              <span className="text-sm text-[#64748B]">Unidades en stock</span>
              <span className="text-lg font-bold text-[#0F172A] font-heading">{fmtNumber(data.stock_total)}</span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-sm text-[#64748B]">Lotes registrados</span>
              <span className="text-lg font-bold text-[#0F172A] font-heading">{fmtNumber(data.total_lotes)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Top ventas / Menos ventas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ListCard
          testid="top-ventas"
          title="Top 10 más vendidos"
          subtitle="Mayor rotación (últimos 12 meses)"
          icon={TrendUp}
          accent={{ bg: "#ECFDF5", fg: "#065F46" }}
          hasItems={!!(ranking && ranking.top_vendidos.length)}
          empty="Sin datos de ventas."
        >
          {ranking &&
            ranking.top_vendidos.map((r, i) => (
              <RankRow
                key={r.id}
                index={i + 1}
                nombre={r.nombre}
                sub={r.numero_lote}
                value={`${fmtNumber(r.total_vendido)} u`}
                valueColor="#065F46"
              />
            ))}
        </ListCard>

        <ListCard
          testid="menos-ventas"
          title="Top 10 menos vendidos"
          subtitle="Menor rotación (últimos 12 meses)"
          icon={TrendDown}
          accent={{ bg: "#FEF2F2", fg: "#991B1B" }}
          hasItems={!!(ranking && ranking.menos_vendidos.length)}
          empty="Sin datos de ventas."
        >
          {ranking &&
            ranking.menos_vendidos.map((r, i) => (
              <RankRow
                key={r.id}
                index={i + 1}
                nombre={r.nombre}
                sub={r.numero_lote}
                value={`${fmtNumber(r.total_vendido)} u`}
                valueColor="#991B1B"
              />
            ))}
        </ListCard>
      </div>

      {/* Resumen de stock: 5 mayor / 5 menor */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ListCard
          testid="mayor-stock"
          title="5 productos con mayor stock"
          subtitle="Stock total acumulado por producto"
          icon={Stack}
          accent={{ bg: "#EFF6FF", fg: "#1E40AF" }}
          hasItems={data.productos_top_stock && data.productos_top_stock.length > 0}
          empty="Sin productos."
        >
          {data.productos_top_stock &&
            data.productos_top_stock.map((p, i) => (
              <RankRow
                key={p.id}
                index={i + 1}
                nombre={p.nombre}
                sub={`${p.codigo} · ${fmtNumber(p.num_lotes)} lote(s)`}
                value={`${fmtNumber(p.stock_total)} u`}
                valueColor="#1E40AF"
              />
            ))}
        </ListCard>

        <ListCard
          testid="menor-stock"
          title="5 productos con menor stock"
          subtitle="Stock total acumulado por producto"
          icon={Package}
          accent={{ bg: "#FFF7ED", fg: "#9A3412" }}
          hasItems={data.productos_low_stock && data.productos_low_stock.length > 0}
          empty="Sin productos."
        >
          {data.productos_low_stock &&
            data.productos_low_stock.map((p, i) => (
              <RankRow
                key={p.id}
                index={i + 1}
                nombre={p.nombre}
                sub={`${p.codigo} · ${fmtNumber(p.num_lotes)} lote(s)`}
                value={`${fmtNumber(p.stock_total)} u`}
                valueColor="#9A3412"
              />
            ))}
        </ListCard>
      </div>

      {/* Sugerencias de liquidación */}
      <ListCard
        testid="liquidacion"
        title="Sugerencias de liquidación"
        subtitle="Lotes de baja rotación próximos a vencer con stock disponible"
        icon={Tag}
        accent={{ bg: "#FDF4FF", fg: "#86198F" }}
        hasItems={!!(ranking && ranking.sugerencias_liquidacion.length)}
        empty="✓ No hay lotes que requieran liquidación."
      >
        {ranking &&
          ranking.sugerencias_liquidacion.map((r) => (
            <li key={r.id} className="flex items-center gap-3 px-5 py-3 hover:bg-[#F8F9FA] transition-colors">
              <span className="w-8 h-8 rounded-md bg-[#FDF4FF] flex items-center justify-center shrink-0">
                <Tag size={15} weight="bold" className="text-[#86198F]" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-[#0F172A] text-sm truncate">{r.nombre}</div>
                <div className="text-xs text-[#64748B]">{r.numero_lote}</div>
              </div>
              <span className="text-xs text-[#64748B] shrink-0">Stock: {fmtNumber(r.stock_actual)}</span>
              <span className="text-xs font-bold text-[#86198F] shrink-0 ml-3">
                Vence en {r.dias_hasta_vencimiento} d
              </span>
            </li>
          ))}
      </ListCard>

      <div className="bg-white border border-[#E2E8F0] rounded-md">
        <div className="px-6 py-4 border-b border-[#E2E8F0] flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold text-[#0F172A]">Últimos movimientos</h2>
          <Link to="/movimientos" className="text-sm font-semibold text-[#475569] hover:text-[#0F172A]">
            Ver todos
          </Link>
        </div>
        {data.ultimos_movimientos.length === 0 ? (
          <p className="px-6 py-10 text-center text-[#64748B]">Aún no hay movimientos.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="dashboard-movimientos-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-[#64748B]">
                  <th className="px-6 py-3 font-semibold">Lote</th>
                  <th className="px-6 py-3 font-semibold">Tipo</th>
                  <th className="px-6 py-3 font-semibold">Motivo</th>
                  <th className="px-6 py-3 font-semibold text-right">Cantidad</th>
                  <th className="px-6 py-3 font-semibold text-right">Fecha</th>
                </tr>
              </thead>
              <tbody>
                {data.ultimos_movimientos.map((m) => (
                  <tr key={m.id} className="border-t border-[#E2E8F0] hover:bg-[#F8F9FA] transition-colors">
                    <td className="px-6 py-3">
                      <div className="font-medium text-[#0F172A]">{m.lote_nombre}</div>
                      <div className="text-xs text-[#64748B]">{m.numero_lote}</div>
                    </td>
                    <td className="px-6 py-3">
                      <span
                        className={`inline-flex items-center gap-1 text-xs font-bold ${
                          m.tipo === "ingreso" ? "text-[#065F46]" : "text-[#991B1B]"
                        }`}
                      >
                        {m.tipo === "ingreso" ? <ArrowDown size={14} weight="bold" /> : <ArrowUp size={14} weight="bold" />}
                        {m.tipo === "ingreso" ? "Ingreso" : "Salida"}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-[#475569]">{m.motivo}</td>
                    <td className="px-6 py-3 text-right font-semibold text-[#0F172A]">{fmtNumber(m.cantidad)}</td>
                    <td className="px-6 py-3 text-right text-[#64748B]">{fmtDate(m.fecha_movimiento)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <OrdenCompraDialog open={!!ocProducto} onOpenChange={(v) => !v && setOcProducto(null)} producto={ocProducto} />
    </div>
  );
}
